import { getOasisAssessmentNonClinicalValuesOptions, type OasisAssessmentViewModel, type OasisNonClinicalValueViewModel } from '@generated';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { Button } from '~/shared/components/buttons/Button';
import { SmallSection } from '~/shared/section/SmallSection';

export function OasisTabNonClinicalDetails({ oasisAssessment }: { oasisAssessment: OasisAssessmentViewModel }) {
	const { data } = useSuspenseQuery(getOasisAssessmentNonClinicalValuesOptions(oasisAssessment.id));
	const itemsBySection = data.items.reduce((acc, item) => {
		(acc[item.sectionLetter] ??= []).push(item);
		return acc;
	}, {} as Record<string, OasisNonClinicalValueViewModel[]>);

	return (
		<div className="flex flex-col gap-4">
			{oasisAssessment.oasisAssessmentDefinition.oasisAssessmentSections
				.filter(section => itemsBySection[section.letter]?.length > 0)
				.map(section => {
					const items = itemsBySection[section.letter] ?? [];
					return (
						<SmallSection
							key={section.letter}
							title={`${section.letter}. ${section.label}`}
							subtitle={`(${items.length} items)`}
							useExpanded
							expandedOnLoad
						>
							<div className="divide-y divide-gray-100">
								{items.map(item => (
									<OasisNonClinicalValue key={item.systemName} item={item} />
								))}
							</div>
						</SmallSection>
					);
				})}
		</div>
	);
}

function OasisNonClinicalValue({ item }: { item: OasisNonClinicalValueViewModel }) {
	return (
		<div className="flex items-start gap-3 px-4 py-2.5">
			<span className="w-24 shrink-0 pt-0.5 font-mono text-[10px] font-semibold text-gray-400">{item.systemName}</span>
			<dl className="grid min-w-0 flex-1 grid-cols-[minmax(0,14rem)_1fr] gap-x-3 gap-y-1">
				<dt className="text-[11px] text-gray-500" dangerouslySetInnerHTML={{ __html: item.label }} />
				<dd className="text-xs text-gray-800">
					{item.value ? (
						<span dangerouslySetInnerHTML={{ __html: item.value }} />
					) : (
						<span className="italic text-gray-300">Not available</span>
					)}
				</dd>
			</dl>
			<div className="shrink-0">
				<Button label="Edit" Icon={Pencil} size="xs" appearance="outline" intent="neutral" />
			</div>
		</div>
	);
}
